import { ArrowDropDown, Cached, Refresh } from '@mui/icons-material';
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useRef, useState } from 'react';

import { ButtonIcons } from '@/components/ButtonIcons';
import { useAlertsStore } from '@/configs/store';

export type SyncMenuItem = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => Promise<void> | void;
  disabled?: boolean;
};

export type SyncConfig = {
  onSync: () => Promise<void> | void;
  tooltip?: string;
  menuItems?: SyncMenuItem[];
  isBusy?: boolean;
};

const splitButtonBase = {
  color: 'var(--secondary-color)',
  minHeight: '32px',
  padding: 0,
  border: 'none',
  borderRadius: 0,
  '&:hover': {
    border: 'none',
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  '&.Mui-disabled': {
    border: 'none',
  },
} as const;

type SyncButtonProps = {
  config: SyncConfig;
};

const SyncButton: React.FC<SyncButtonProps> = observer(({ config }) => {
  const { onSync, tooltip = 'Обновить', menuItems, isBusy } = config;
  const alertsStore = useAlertsStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleSync = useCallback(async () => {
    try {
      await onSync();
    } catch {
      alertsStore.addAlert({
        title: 'Ошибка',
        message: 'Не удалось обновить данные',
        autoClose: true,
        withReload: false,
      });
    }
  }, [onSync, alertsStore]);

  const handleMenuItemClick = useCallback(
    async (item: SyncMenuItem) => {
      setMenuOpen(false);
      try {
        await item.onClick();
      } catch {
        alertsStore.addAlert({
          title: 'Ошибка',
          message: 'Не удалось выполнить операцию',
          autoClose: true,
          withReload: false,
        });
      }
    },
    [alertsStore],
  );

  // Simple mode: just an icon button
  if (!menuItems || menuItems.length === 0) {
    return (
      <Tooltip title={tooltip} arrow>
        <ButtonIcons onClick={handleSync}>
          <Cached />
        </ButtonIcons>
      </Tooltip>
    );
  }

  // Split button mode: primary action + dropdown
  return (
    <Box sx={{ marginLeft: '12px', display: 'inline-flex' }}>
      <Tooltip title={tooltip} arrow>
        <Box
          sx={{
            display: 'inline-flex',
            border: '1px solid var(--secondary-color)',
            borderRadius: '6px',
            overflow: 'hidden',
          }}
        >
          <Button
            variant="text"
            onClick={handleSync}
            disabled={isBusy}
            sx={{ ...splitButtonBase, minWidth: '32px' }}
          >
            <Refresh
              sx={{
                fontSize: 20,
                ...(isBusy && {
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                }),
              }}
            />
          </Button>
          <Box
            sx={{
              width: '1px',
              alignSelf: 'stretch',
              backgroundColor: 'var(--secondary-color)',
              opacity: 0.5,
            }}
          />
          <Button
            variant="text"
            ref={anchorRef}
            onClick={() => setMenuOpen(true)}
            disabled={isBusy}
            sx={{ ...splitButtonBase, minWidth: '20px' }}
          >
            <ArrowDropDown sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Tooltip>
      <Menu
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => handleMenuItemClick(item)}
            disabled={item.disabled || isBusy}
          >
            {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
});

export { SyncButton };
